import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import {
  ShieldCheck, Gauge, ClipboardCheck, Activity, ScrollText, Database,
  CheckCircle2, AlertTriangle, XCircle, Play, RefreshCw, Scale,
} from 'lucide-react';
import { AriaAutonomy } from '@/components/AriaAutonomy';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', saffron: '#FF9933' };
const DECISION_COLORS = { APPROVE: C.green, CONDITIONS: C.blue, REFER: C.amber, DECLINE: C.red };

function gradeColor(g) {
  if (g === 'A') return C.green;
  if (g === 'B') return C.blue;
  if (g === 'C') return C.amber;
  return C.red;
}

const Metric = ({ label, value, suffix, color, testid }) => (
  <div className="flex flex-col" data-testid={testid}>
    <span className="text-3xl font-bold font-mono" style={{ color: color || 'white' }}>{value}<span className="text-lg">{suffix}</span></span>
    <span className="text-xs uppercase tracking-wider mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>{label}</span>
  </div>
);

const Section = ({ icon: Icon, title, subtitle, action, children, testid }) => (
  <Card className="p-5 rounded-lg bg-white border" data-testid={testid}>
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5" style={{ color: C.blue }} />
        <div>
          <h3 className="text-base font-semibold" style={{ color: C.navy }}>{title}</h3>
          {subtitle && <p className="text-xs" style={{ color: C.slate }}>{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
    {children}
  </Card>
);

const StatusPill = ({ status }) => {
  const map = { PASS: C.green, A: C.green, stable: C.green, CLEAR: C.green, WARNING: C.amber, REVIEW: C.amber, B: C.blue, C: C.amber, FAIL: C.red, drift_detected: C.red };
  const color = map[status] || C.slate;
  return <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: `${color}22`, color }}>{status}</span>;
};

export default function GovernancePage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [includeLlm, setIncludeLlm] = useState(false);
  const [biasHistory, setBiasHistory] = useState([]);

  const load = useCallback(() => {
    api.get('/governance/bias/history').then(r => setBiasHistory(r.data.history || [])).catch(() => {});
    return api.get('/governance/overview')
      .then(r => setData(r.data))
      .catch(err => console.error('governance overview:', err?.message))
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, label) => {
    setBusy(key);
    try {
      await fn();
      toast.success(`${label} complete`);
      await load();
    } catch (e) {
      toast.error(`${label} failed: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setBusy('');
    }
  };

  const seedIfNeeded = () => run('seed', () => api.post('/governance/benchmark/seed'), 'Benchmark seed');
  const genScorecard = () => run('scorecard', () => api.post('/governance/scorecard/generate', { llm_sample: includeLlm ? 25 : 0 }), 'Scorecard');
  const runAudit = () => run('audit', () => api.post('/governance/audit/run?sample=550'), 'Audit');
  const runCompliance = () => run('compliance', () => api.post('/governance/compliance/review?sample=200'), 'Compliance review');
  const detectDrift = () => run('drift', () => api.post('/governance/drift/detect'), 'Drift detection');
  const runBias = () => run('bias', () => api.post('/governance/bias/analyze'), 'Bias analysis');

  if (loading) return <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28 rounded-md" />)}</div>;

  const sc = data?.latest_scorecard;
  const audit = data?.latest_audit;
  const comp = data?.latest_compliance;
  const alerts = data?.drift_alerts || [];
  const bench = data?.benchmark || { total: 0, by_category: {} };
  const bias = data?.latest_bias;
  const h = sc?.headline;
  const dist = sc?.distribution?.decision_rates_pct || {};

  return (
    <div className="space-y-5" data-testid="governance-page">
      <AriaAutonomy />
      {/* Hero scorecard */}
      <Card className="p-6 rounded-lg" style={{ background: 'linear-gradient(135deg, #0B1E3D 0%, #15375E 100%)', border: 'none' }} data-testid="governance-hero">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold flex items-center gap-2" style={{ color: C.saffron }}>
              <ShieldCheck className="w-4 h-4" /> AI Governance Layer
            </p>
            {h ? (
              <div className="flex items-end gap-8 mt-3">
                <div className="flex flex-col">
                  <span className="text-6xl font-bold font-mono" style={{ color: gradeColor(h.grade) }} data-testid="governance-grade">{h.grade}</span>
                  <span className="text-xs uppercase tracking-wider mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>Scorecard grade</span>
                </div>
                <Metric label="Decision accuracy" value={h.decision_accuracy_pct} suffix="%" color={C.teal} testid="metric-accuracy" />
                <Metric label="Consistency" value={h.consistency_pct} suffix="%" color={C.green} testid="metric-consistency" />
                <Metric label="Failure rate" value={h.failure_rate_pct} suffix="%" color={h.failure_rate_pct < 1 ? C.green : C.red} testid="metric-failure" />
              </div>
            ) : (
              <p className="text-sm mt-3" style={{ color: 'rgba(255,255,255,0.8)' }}>No scorecard yet — run your first benchmark evaluation.</p>
            )}
            {sc?.created_at && (() => {
              const days = Math.floor((Date.now() - new Date(sc.created_at)) / 86400000);
              return (
                <p className="text-xs mt-3" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  Last run {new Date(sc.created_at).toLocaleString('en-AU')} · {sc.benchmark_size} scenarios
                  {days > 30 && <span style={{ color: '#E8A020' }}> · {days} days old — re-run recommended</span>}
                </p>
              );
            })()}
          </div>
          <div className="flex flex-col gap-3 lg:items-end">
            <div className="flex items-center gap-2" data-testid="llm-toggle">
              <Switch checked={includeLlm} onCheckedChange={setIncludeLlm} id="llm-eval" data-testid="llm-eval-switch" />
              <label htmlFor="llm-eval" className="text-xs" style={{ color: 'rgba(255,255,255,0.8)' }}>Include LLM-agent eval (uses tokens)</label>
            </div>
            <Button onClick={genScorecard} disabled={!!busy} data-testid="run-scorecard-btn" style={{ background: C.saffron, color: C.navy }}>
              {busy === 'scorecard' ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />} Generate Scorecard
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Benchmark dataset */}
        <Section icon={Database} title="Benchmark Dataset" subtitle={`${bench.total} underwriting scenarios`} testid="section-benchmark"
          action={bench.total === 0 && <Button size="sm" onClick={seedIfNeeded} disabled={!!busy} data-testid="seed-benchmark-btn">Seed</Button>}>
          <div className="grid grid-cols-3 gap-3">
            {['normal', 'edge', 'failure'].map(cat => (
              <div key={cat} className="rounded-md p-3 text-center" style={{ background: '#F4F6F9' }} data-testid={`bench-${cat}`}>
                <div className="text-2xl font-bold font-mono" style={{ color: C.navy }}>{bench.by_category?.[cat] || 0}</div>
                <div className="text-xs capitalize" style={{ color: C.slate }}>{cat}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* Agent Evaluator — by category */}
        <Section icon={Gauge} title="Agent Evaluator" subtitle="Accuracy by scenario class" testid="section-evaluator">
          {sc ? (
            <div className="space-y-2">
              {Object.entries(sc.engine?.by_category || {}).map(([cat, m]) => (
                <div key={cat} className="flex items-center justify-between text-sm" data-testid={`eval-${cat}`}>
                  <span className="capitalize" style={{ color: C.navy }}>{cat} <span style={{ color: C.slate }}>({m.total})</span></span>
                  <div className="flex items-center gap-4">
                    <span style={{ color: C.slate }}>acc <b style={{ color: pctC(m.decision_accuracy_pct) }}>{m.decision_accuracy_pct}%</b></span>
                    <span style={{ color: C.slate }}>fail <b style={{ color: m.failure_rate_pct < 1 ? C.green : C.red }}>{m.failure_rate_pct}%</b></span>
                  </div>
                </div>
              ))}
            </div>
          ) : <Empty />}
        </Section>

        {/* Decision distribution */}
        <Section icon={Activity} title="Decision Distribution" subtitle="Across the benchmark" testid="section-distribution">
          {sc ? (
            <div className="space-y-2">
              {['APPROVE', 'CONDITIONS', 'REFER', 'DECLINE'].map(d => (
                <div key={d} data-testid={`dist-${d}`}>
                  <div className="flex justify-between text-xs mb-1"><span style={{ color: C.navy }}>{d}</span><span style={{ color: C.slate }}>{dist[d] || 0}%</span></div>
                  <div className="h-2 rounded-full" style={{ background: '#EEF1F5' }}>
                    <div className="h-2 rounded-full" style={{ width: `${dist[d] || 0}%`, background: DECISION_COLORS[d] }} />
                  </div>
                </div>
              ))}
            </div>
          ) : <Empty />}
        </Section>

        {/* Agent Auditor */}
        <Section icon={ClipboardCheck} title="Agent Auditor" subtitle="Pass / Warning / Fail" testid="section-auditor"
          action={<Button size="sm" variant="outline" onClick={runAudit} disabled={!!busy} data-testid="run-audit-btn">{busy === 'audit' ? '…' : 'Run'}</Button>}>
          {audit ? (
            <div>
              <div className="flex gap-4 mb-3">
                <Tally icon={CheckCircle2} color={C.green} label="Pass" n={audit.counts?.PASS} testid="audit-pass" />
                <Tally icon={AlertTriangle} color={C.amber} label="Warning" n={audit.counts?.WARNING} testid="audit-warning" />
                <Tally icon={XCircle} color={C.red} label="Fail" n={audit.counts?.FAIL} testid="audit-fail" />
              </div>
              {Object.keys(audit.finding_tally || {}).length > 0 && (
                <div className="text-xs space-y-1" style={{ color: C.slate }}>
                  {Object.entries(audit.finding_tally).map(([k, v]) => <div key={k}>{k}: <b>{v}</b></div>)}
                </div>
              )}
            </div>
          ) : <Empty hint="Run the auditor over the benchmark." />}
        </Section>

        {/* Drift Detection */}
        <Section icon={Activity} title="Drift Detection" subtitle="Distribution & quality drift" testid="section-drift"
          action={<Button size="sm" variant="outline" onClick={detectDrift} disabled={!!busy} data-testid="run-drift-btn">{busy === 'drift' ? '…' : 'Detect'}</Button>}>
          {alerts.length === 0 ? (
            <div className="flex items-center gap-2 text-sm" data-testid="drift-stable"><CheckCircle2 className="w-4 h-4" style={{ color: C.green }} /><span style={{ color: C.navy }}>No drift alerts — distributions stable.</span></div>
          ) : (
            <div className="space-y-2">
              {alerts.slice(0, 5).map((a, i) => (
                <div key={a.checked_at || i} className="text-sm flex justify-between" data-testid={`drift-alert-${i}`}>
                  <span style={{ color: C.navy }}>{a.label || a.status}</span>
                  <StatusPill status={a.status || 'drift'} />
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Compliance Review */}
        <Section icon={ScrollText} title="Compliance Review" subtitle="Reproducible & auditable" testid="section-compliance"
          action={<Button size="sm" variant="outline" onClick={runCompliance} disabled={!!busy} data-testid="run-compliance-btn">{busy === 'compliance' ? '…' : 'Run'}</Button>}>
          {comp ? (
            <div className="flex items-center justify-between">
              <div className="flex gap-5 text-sm">
                <KV label="Reproducible" v={`${comp.reproducible_pct}%`} />
                <KV label="Evidence" v={`${comp.evidence_complete_pct}%`} />
                <KV label="Auditable" v={`${comp.auditable_pct}%`} />
              </div>
              <StatusPill status={comp.status} />
            </div>
          ) : <Empty hint="Confirm decisions are reproducible & auditable." />}
        </Section>
      </div>

      {/* Bias / Disparate-Impact Detection — full width */}
      <Section icon={Scale} title="Bias / Disparate-Impact Detection"
        subtitle="Favourable-outcome rates by industry, region & size (4/5ths rule)" testid="section-bias"
        action={
          <div className="flex items-center gap-3">
            {bias?.status && <StatusPill status={bias.status} />}
            <Button size="sm" variant="outline" onClick={runBias} disabled={!!busy} data-testid="run-bias-btn">
              {busy === 'bias' ? '…' : 'Analyze'}
            </Button>
          </div>
        }>
        {bias?.dimensions ? (
          <div className="space-y-4" data-testid="bias-content">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs" style={{ color: C.slate }}>
              <span>Sample <b style={{ color: C.navy }}>{bias.sample_size}</b></span>
              <span>Flags <b style={{ color: bias.total_flags ? C.amber : C.green }}>{bias.total_flags}</b></span>
              <span>Severity <b style={{ color: bias.highest_severity === 'HIGH' ? C.red : bias.highest_severity === 'MEDIUM' ? C.amber : C.green }}>{bias.highest_severity}</b></span>
            </div>
            {biasHistory.length >= 2 && (
              <div className="rounded-md p-3" style={{ background: '#F4F6F9' }} data-testid="bias-trend">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Fairness Trend — worst impact ratio</span>
                  <span className="text-[10px]" style={{ color: C.slate }}>0.80 = 4/5ths threshold</span>
                </div>
                <ResponsiveContainer width="100%" height={140}>
                  <LineChart data={biasHistory.map((s, i) => ({
                    idx: i + 1,
                    industry: s.dimensions?.industry?.worst_impact_ratio,
                    region: s.dimensions?.region?.worst_impact_ratio,
                    size: s.dimensions?.size?.worst_impact_ratio,
                  }))} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <XAxis dataKey="idx" tick={{ fontSize: 10, fill: C.slate }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 1]} tick={{ fontSize: 10, fill: C.slate }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                    <ReferenceLine y={0.8} stroke={C.red} strokeDasharray="4 3" />
                    <Line type="monotone" dataKey="industry" stroke="#1A4C7C" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="region" stroke="#00B4D8" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="size" stroke="#E8A020" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
                <div className="flex gap-4 text-[10px] mt-1" style={{ color: C.slate }}>
                  <span><b style={{ color: '#1A4C7C' }}>—</b> industry</span>
                  <span><b style={{ color: '#00B4D8' }}>—</b> region</span>
                  <span><b style={{ color: '#E8A020' }}>—</b> size</span>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {['industry', 'region', 'size'].map(dim => {
                const d = bias.dimensions[dim];
                if (!d) return null;
                const segs = Object.entries(d.segments).sort((a, b) => b[1].favourable_rate_pct - a[1].favourable_rate_pct);
                return (
                  <div key={dim} className="rounded-md p-3" style={{ background: '#F4F6F9' }} data-testid={`bias-dim-${dim}`}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-semibold uppercase tracking-wider capitalize" style={{ color: C.navy }}>{dim}</span>
                      <span className="text-[10px]" style={{ color: C.slate }}>ref {d.reference_rate_pct}%</span>
                    </div>
                    <div className="space-y-2">
                      {segs.map(([key, seg]) => (
                        <div key={key} data-testid={`bias-seg-${dim}-${key}`}>
                          <div className="flex justify-between text-[11px] mb-0.5">
                            <span style={{ color: C.navy }}>{key} <span style={{ color: C.slate }}>({seg.total})</span></span>
                            <span style={{ color: seg.disparate_impact ? C.red : C.slate }}>
                              {seg.favourable_rate_pct}%{seg.disparate_impact ? ` · ${seg.impact_ratio}×` : ''}
                            </span>
                          </div>
                          <div className="h-1.5 rounded-full" style={{ background: '#E3E8EE' }}>
                            <div className="h-1.5 rounded-full" style={{ width: `${seg.favourable_rate_pct}%`, background: seg.disparate_impact ? C.red : C.green }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] leading-relaxed" style={{ color: C.slate }}>{bias.note}</p>
          </div>
        ) : <Empty hint="Run the analysis to surface segments with materially lower favourable-outcome rates for human review." />}
      </Section>
    </div>
  );
}

function pctC(p) { return p >= 90 ? C.green : p >= 75 ? C.blue : p >= 60 ? C.amber : C.red; }
const Empty = ({ hint }) => <p className="text-sm" style={{ color: C.slate }}>{hint || 'Generate a scorecard to populate this section.'}</p>;
const Tally = ({ icon: Icon, color, label, n, testid }) => (
  <div className="flex items-center gap-2" data-testid={testid}>
    <Icon className="w-5 h-5" style={{ color }} />
    <div><div className="text-xl font-bold font-mono" style={{ color: C.navy }}>{n ?? 0}</div><div className="text-xs" style={{ color: C.slate }}>{label}</div></div>
  </div>
);
const KV = ({ label, v }) => (
  <div><div className="text-lg font-bold font-mono" style={{ color: C.navy }}>{v}</div><div className="text-xs" style={{ color: C.slate }}>{label}</div></div>
);
